// Common
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  Injector,
  type SimpleChanges,
  inject,
  signal,
} from '@angular/core';

// Services
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { StateService } from '@modules/settings/services/state.service';
import { StitchService } from '@modules/common/services/stitch.service';
import { TaskingAppSettingsService } from '@modules/tasks/services/settings.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { BoardService } from '@modules/tasks/services/board.service';

// RX
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

// Types
import { DragData, DragDataTypes, taskStitchTargetPredicate } from '@modules/drag-n-drop/types/drag-data';
import { Task } from '@modules/tasks/types/task';
import {
  getStitchDateZone,
  getStitchOverdueDate,
  isStitchBoundaryFloating,
} from '@modules/tasks/utils/stitch-time.util';
import { isOverdue } from '@sharedlib/common/utils/date';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { LinkedInfo } from '@modules/linked-info/types/linked-info';

@Component({
  selector: 'app-board-task',
  templateUrl: './board-task.component.html',
  styleUrls: ['./board-task.component.less'],
  standalone: false,
})
export class BoardTaskComponent implements OnInit, OnChanges, OnDestroy {
  public isDragging = false;
  public dragData: DragData = { data: [], type: DragDataTypes.task };
  public showIssueKey = false;

  private alive = new Subject<void>();
  private readonly taskChanged = new Subject<void>();

  /** Items already stitched to this task; read by dndPredicate to reject re-stitching an already-linked item. */
  protected linkedInfo: LinkedInfo[] = [];

  @Input() task: Task;
  @Input() dragging = false;
  @Input() subtask = false;

  protected readonly isOverdue = signal(false);

  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  // SBUX-829: a boundary's date renders in the account zone when it carries a time; a floating
  // (all-day / date-only) boundary renders as its bare calendar day (UTC — never a browser or
  // account shift). Recomputed on task change.
  protected startDateZone = 'UTC';
  protected endDateZone = 'UTC';

  @Input() active: boolean;
  @Input() index: number;
  @Input() position: number;

  @Output() open = new EventEmitter();
  @Output() openSubtask = new EventEmitter<Task>();

  // A board task is a stitch target for any non-task stitchable item dropped onto it (a task
  // drag is reordering, owned by the column). Registered above the project drop zone (zIndex 1)
  // so hovering a task stitches into the task, while the surrounding panel stitches into the project.
  protected readonly dndPredicate = (dragData: DragData): boolean =>
    taskStitchTargetPredicate(dragData, this.linkedInfo);

  constructor(
    protected stateService: StateService,
    private settingsService: TaskingAppSettingsService,
    private tasksService: TasksService,
    private injector: Injector,
    private modalService: ModalService,
    private dpService: DynamicPanelService,
    private stitchService: StitchService,
    private linkedInfoService: LinkedInfoService,
    private boardService: BoardService,
  ) {}

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.dragData = { data: [this.task], type: DragDataTypes.task };

    this.settingsService
      .listAll()
      .pipe(takeUntil(this.alive))
      .subscribe(({ issueKeyEnabled }) => (this.showIssueKey = issueKeyEnabled));

    this.linkedInfoService
      .trackLinkedInfo(this.taskChanged.pipe(startWith(undefined)), () => this.task)
      .pipe(takeUntil(this.alive))
      .subscribe((info) => (this.linkedInfo = info));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('task' in changes) {
      const accountZone = this.displayTimeZoneService.timeZone();
      const overdue = isOverdue({
        // SBUX-829: see the same call in `task.component.ts` — account zone on both sides.
        dueAt: getStitchOverdueDate(this.task, 'end', accountZone),
        floating: isStitchBoundaryFloating(this.task, 'end'),
        completed: this.task?.completed,
        timeZone: accountZone,
      });
      this.isOverdue.set(overdue);
      this.startDateZone = getStitchDateZone(this.task, 'start', accountZone);
      this.endDateZone = getStitchDateZone(this.task, 'end', accountZone);
      this.taskChanged.next();
    }
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Writes only completion, as the menu's Mark complete does: the card in memory does not learn a Pin or
   * Flag saved from its menu, so sending the whole card would put the stale values back.
   */
  handleComplete() {
    this.task.completed = !this.task.completed;
    this.tasksService.update(this.task, {
      ...this.boardService.itemRequestConfig(),
      updatePayload: { completed: this.task.completed },
    });
  }

  /**
   * Actions
   */

  openInModal(): void {
    if (!this.task) return;
    this.modalService.openFullForm(this.task, this.injector, this.boardService.itemRequestConfig().requestContext);
  }

  openInDP(): void {
    if (!this.task) return;
    this.dpService.setFormItem(this.task, true, this.boardService.itemRequestConfig().requestContext);
  }

  public dndDrop(dragData: DragData): void {
    this.stitchService.linkDragData(this.task, dragData);
  }
}
